import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { ViewportService } from '@app/core/_services/viewport.service'

const ALLOWED_CALENDAR_MODE_VALUES = ['1d', '2d', '3d', '5d', '7d', '1w'] as const
export type CalendarViewMode = (typeof ALLOWED_CALENDAR_MODE_VALUES)[number]

const MAP_CALENDAR_MODE_TO_NUMBER_OF_DAYS: Record<CalendarViewMode, number> = {
    '1d': 1,
    '2d': 2,
    '3d': 3,
    '5d': 5,
    '7d': 7,
    '1w': 7,
}

const MAP_NUMBER_OF_DAYS_TO_CALENDAR_MODE: Record<number, CalendarViewMode> = {
    1: '1d',
    2: '2d',
    3: '3d',
    5: '5d',
    7: '7d',
}

export interface CalendarRange {
    startTime: number
    numberOfDaysToShow: number
}

@Injectable({
    providedIn: 'root',
})
export class CalendarViewService {
    // Subject to emit view setting changes
    private calendarRangeSubject = new BehaviorSubject<CalendarRange | null>(null)
    calendarRange$ = this.calendarRangeSubject.asObservable()

    private todaySelectedSubject = new BehaviorSubject<null>(null)
    public todaySelected$ = this.todaySelectedSubject.asObservable()

    public triggerTodaySelected$ = new Subject<void>()

    constructor(
        private viewportService: ViewportService,
        private localStorageService: LocalStorageAccessService,
    ) {}

    private isCalendarViewMode(mode: string): mode is CalendarViewMode {
        return ALLOWED_CALENDAR_MODE_VALUES.includes(mode as CalendarViewMode)
    }

    // Helper method to get the initial value for the BehaviorSubject
    public getInitialCalendarMode(): CalendarViewMode {
        const storedValue = this.localStorageService.getItem('calendarMode')

        if (storedValue !== null) {
            if (this.isCalendarViewMode(storedValue)) {
                return storedValue
            }
        } else {
            const defaultCalendarMode = this.getDefaultCalendarMode(this.viewportService.width())
            this.localStorageService.setItem('calendarMode', defaultCalendarMode)
            return defaultCalendarMode
        }
    }

    public getNumberOfdaysForCalendarMode(mode: CalendarViewMode): number {
        return MAP_CALENDAR_MODE_TO_NUMBER_OF_DAYS[mode]
    }

    public getCalendarModeFromNumberOfDays(
        numberOfDays: number,
        weekModeEnabled: boolean,
    ): CalendarViewMode {
        if (numberOfDays === 7 && weekModeEnabled) {
            return '1w'
        } else {
            return MAP_NUMBER_OF_DAYS_TO_CALENDAR_MODE[numberOfDays]
        }
    }

    public storeCalendarMode(mode: CalendarViewMode): void {
        this.localStorageService.setItem('calendarMode', mode)
    }

    emitCalendarRange(range: CalendarRange) {
        this.calendarRangeSubject.next(range)
    }

    emitTodaySelected() {
        this.todaySelectedSubject.next(null)
    }

    triggerTodaySelected() {
        this.triggerTodaySelected$.next()
    }

    /**
     * Returns the *maximum* number of calendar days the UI should let the user pick,
     * based on the current viewport width.
     */
    public getMaxDaysToShow(width: number): 1 | 3 | 5 | 7 {
        if (width < 480) return 1 // 320 – 479
        if (width < 760) return 3 // 480 – 559
        if (width < 908) return 5 // 560 – 893
        if (width <= 1099) return 3 // 894 – 1099
        if (width <= 1279) return 5 // 1100 – 1279
        if (width <= 1535) return 7 // 1280 – 1535
        /* ≥1536 px */ return 7 // ultrawide
    }

    /**
     * Returns the *default* number of days the UI should show when the view first
     * opens (before the user changes anything), based on the viewport width.
     *
     * NOTE: The 481 – 560 px range has “TBD” in your spec.
     * Until that’s finalised I’ve used **1** so the logic still compiles.
     * Change the value in that `if` branch whenever you decide.
     */
    public getDefaultCalendarMode(width: number): CalendarViewMode {
        if (width < 480) return '1d' // 320 – 479
        if (width < 760) return '2d' // 480 – 559
        if (width < 908) return '3d' // 560 – 893
        if (width <= 1099) return '3d' // 894 – 1099
        if (width <= 1279) return '3d' // 1100 – 1279
        if (width <= 1535) return '3d' // 1280 – 1535
        /* ≥1536 px */ return '3d' // ultrawide
    }
}
